import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { createApi } from '@/features/utils';
import { FiltersItemType, FiltersRawItemType } from '@/interfaces/filters.interfaces';
import { DataTypeEnum } from '@/interfaces/settings.interfaces';

import filtersChoiceSelector from '../choice/selectors/filters';
import isFiltersSelectedSelector from '../choice/selectors/isFiltersSelected';
import flattenFilterGridItemsSelector from '../config/selectors/flattenFilterGridItems';
import getExtendedItems from '../references/helpers/getExtendedItems';

import fetchAvailableFilters from './helpers/fetchAvailableFilters';
import fetchFilters from './helpers/fetchFilters';
import fetchFiltersWithExtraData from './helpers/fetchFiltersWithExtraData';
import fetchFiltersWithExtraValues from './helpers/fetchFiltersWithExtraValues';
import formatFiltersRawItem from './helpers/formatFiltersRawItem';
import filtersParametersSelector from './selectors/parameters';
import type { FiltersParametersType } from './types';

export const filtersApi = createApi({
  reducerPath: 'filters',
  baseQuery: fetchBaseQuery(),
  endpoints: builder => ({
    // Получение всех фильтров
    getFilters: builder.query<FiltersRawItemType, FiltersParametersType>({
      queryFn: async (parameters, api) => {
        const filtersRawItem = await fetchFilters(api, parameters);
        const filtersRawWithExtraDataItem = await fetchFiltersWithExtraData(
          api,
          filtersRawItem,
        );

        const filtersRawWithExtraValuesItem = await fetchFiltersWithExtraValues(
          api,
          filtersRawWithExtraDataItem,
        );

        const [extendedFiltersRawItem] = await getExtendedItems(api, [
          filtersRawWithExtraValuesItem,
        ]);

        return {
          data: extendedFiltersRawItem,
        };
      },
    }),
    // Получение фильтров с возможными значениями фильтрации
    getAggregateFilters: builder.query<FiltersItemType, FiltersParametersType>({
      queryFn: async (aggregateParameters, api) => {
        const state = api.getState() as any;

        const { formatter } = state.config;

        const parameters = filtersParametersSelector(state);
        const filtersChoice = filtersChoiceSelector(state);
        const filterGridItems = flattenFilterGridItemsSelector(state);

        const isFiltersSelected = isFiltersSelectedSelector(state);
        const isExistQueries =
          !!aggregateParameters.filter && !!aggregateParameters.filter.q?.length;

        const { data: filtersRawItem } =
          filtersApi.endpoints.getFilters.select(parameters)(state);

        if (!filtersRawItem) {
          throw new Error('Виджет АВН. Отсутствуют данные по фильтрам');
        }

        // Данные по возможным значениям фильтров
        let aggregateFiltersRawItem = filtersRawItem;

        if (isExistQueries && isFiltersSelected) {
          aggregateFiltersRawItem = await fetchAvailableFilters(
            api,
            filtersRawItem.data,
            parameters,
            aggregateParameters,
          );
        }

        const aggregateFiltersRawItemWithExtraValues = await fetchFiltersWithExtraValues(
          api,
          aggregateFiltersRawItem,
        );

        const filtersItem = formatFiltersRawItem(
          filtersRawItem,
          aggregateFiltersRawItemWithExtraValues,
          filtersChoice,
          filterGridItems,
        );

        const formattedFiltersItem = (formatter?.(DataTypeEnum.Filters, filtersItem) ??
          filtersItem) as FiltersItemType;

        return {
          data: formattedFiltersItem,
        };
      },
    }),
  }),
});

const filtersReducer = filtersApi.reducer;

export default filtersReducer;
